import BaseSection from '@/sections/base'
import ProductCard from '@/components/product/productCard'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

const selectors = {
  grid: '[data-product-grid]',
  productLink: '[data-product-link]',
  productCard: '[data-product-card]'
}

const classes = {
  cardZoomTarget: 'is-zoom-target'
}

const GRID_STORAGE_KEY = 'featured-products.grid-columns'
const MOBILE_MEDIA_QUERY = '(max-width: 63.98rem)'

type GridMode = '3' | '6' | '2' | '1' | 'list'

export default class FeaturedProductsSection extends BaseSection {
  static TYPE = 'featured-products'

  productCards: ProductCard[]
  grid: HTMLElement | undefined
  currentMode: GridMode
  transitionLock: boolean
  mobileQuery: MediaQueryList

  onExternalSetColumnsBound: (e: CustomEvent<{ mode?: GridMode; cols?: number }>) => void
  onGridClickBound: (e: MouseEvent) => void
  onViewportChangeBound: () => void

  constructor(container: HTMLElement) {
    super(container)

    this.productCards = this.qsa(ProductCard.SELECTOR).map((el: HTMLElement) => new ProductCard(el))
    this.grid = this.qs(selectors.grid)
    this.mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    this.currentMode = this.normalizeGridMode(this.dataset.defaultCols || '6')
    this.transitionLock = false

    this.onExternalSetColumnsBound = this.onExternalSetColumns.bind(this)
    this.onGridClickBound = this.onGridClick.bind(this)
    this.onViewportChangeBound = this.onViewportChange.bind(this)

    this.initializeGrid()
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    window.removeEventListener('featuredProducts.setColumns', this.onExternalSetColumnsBound as EventListener)
    this.grid?.removeEventListener('click', this.onGridClickBound)
    this.mobileQuery.removeEventListener('change', this.onViewportChangeBound)

    super.onUnload(e)
  }

  isMobileView() {
    return this.mobileQuery.matches
  }

  initializeGrid() {
    if (!this.grid) return

    const storedMode = window.localStorage.getItem(GRID_STORAGE_KEY)
    const resolvedMode = this.normalizeGridMode(storedMode || this.currentMode)

    this.setGridMode(resolvedMode)

    this.grid.style.setProperty('--duration', '280ms')
    this.grid.style.setProperty('--tx', '0px')
    this.grid.style.setProperty('--ty', '0px')
    this.grid.style.setProperty('--scale', '1')
    this.grid.style.setProperty('--opacity', '1')

    window.addEventListener('featuredProducts.setColumns', this.onExternalSetColumnsBound as EventListener)
    this.grid.addEventListener('click', this.onGridClickBound)
    this.mobileQuery.addEventListener('change', this.onViewportChangeBound)
  }

  normalizeGridMode(rawMode: string | GridMode, forceMobileView?: boolean): GridMode {
    const isMobileView = forceMobileView ?? this.isMobileView()

    if (rawMode === 'list') return 'list'

    if (isMobileView) {
      if (rawMode === '2' || rawMode === '1') return rawMode
      if (rawMode === '3') return '2'
      if (rawMode === '6') return '1'
    }
    else {
      if (rawMode === '3' || rawMode === '6') return rawMode
      if (rawMode === '2') return '3'
      if (rawMode === '1') return '6'
    }

    if (rawMode === 'L' || rawMode === 'l') return 'list'

    const parsedMode = Number.parseInt(String(rawMode || (isMobileView ? '2' : '6')), 10)

    if (isMobileView) {
      if (parsedMode === 2 || parsedMode === 3) return '2'
      if (parsedMode === 1 || parsedMode === 6) return '1'

      return '2'
    }

    if (parsedMode === 3 || parsedMode === 2) return '3'
    if (parsedMode === 6 || parsedMode === 1) return '6'

    return '6'
  }

  resolveColCount(mode: GridMode) {
    if (mode === 'list') return 1

    return Number(mode)
  }

  setGridMode(nextMode: GridMode) {
    if (!this.grid) return

    const hasModeApplied = this.grid.dataset.gridMode === nextMode

    if (this.currentMode === nextMode && hasModeApplied) return

    const grid = this.grid
    const normalizedMode = this.normalizeGridMode(nextMode)
    const colCount = this.resolveColCount(normalizedMode)

    this.currentMode = normalizedMode
    this.container.dataset.gridMode = normalizedMode

    // Match Yeezy behavior: col-count is a discrete state switch.
    // Transform/opacity vars stay neutral unless a product is opened.
    grid.style.setProperty('--col-count', `${colCount}`)
    grid.style.setProperty('--list-mode', normalizedMode === 'list' ? '1' : '0')
    grid.dataset.gridMode = normalizedMode
    grid.style.setProperty('--tx', '0px')
    grid.style.setProperty('--ty', '0px')
    grid.style.setProperty('--scale', '1')
    grid.style.setProperty('--opacity', '1')
  }

  onExternalSetColumns(e: CustomEvent<{ mode?: GridMode; cols?: number }>) {
    const nextMode = e.detail?.mode ? this.normalizeGridMode(e.detail.mode) : this.normalizeGridMode(String(e.detail?.cols || '6'))

    this.setGridMode(nextMode)
  }

  onViewportChange() {
    const storedMode = window.localStorage.getItem(GRID_STORAGE_KEY)

    this.setGridMode(this.normalizeGridMode(storedMode || this.currentMode))
  }

  onGridClick(e: MouseEvent) {
    if (!this.grid || this.transitionLock) return

    const target = e.target as HTMLElement
    const link = target.closest(selectors.productLink) as HTMLAnchorElement | null

    if (!link || !link.href) return

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank') return

    const card = link.closest(selectors.productCard) as HTMLElement | null

    if (!card) return

    e.preventDefault()

    this.transitionLock = true

    const rect = card.getBoundingClientRect()
    const cardCenterX = rect.left + (rect.width / 2)
    const cardCenterY = rect.top + (rect.height / 2)
    const viewportCenterX = window.innerWidth / 2
    const viewportCenterY = window.innerHeight / 2

    const tx = (viewportCenterX - cardCenterX) * 0.2
    const ty = (viewportCenterY - cardCenterY) * 0.2

    card.classList.add(classes.cardZoomTarget)

    this.grid.style.setProperty('--duration', '420ms')
    this.grid.style.setProperty('--tx', `${tx}px`)
    this.grid.style.setProperty('--ty', `${ty}px`)
    this.grid.style.setProperty('--scale', '1.12')
    this.grid.style.setProperty('--opacity', '0.28')

    window.setTimeout(() => {
      if (window.app?.taxi) {
        window.app.taxi.navigateTo(link.href)
      }
      else {
        window.location.href = link.href
      }
    }, 280)
  }
}